import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { emptyTipTapDoc } from './tipTapEmpty';
import { LABEL_CLASS } from './ScopeField';

const toolbarBtn =
  'h-8 min-w-8 px-2 rounded-md text-sm font-medium font-["Open_Sans"] border border-transparent hover:bg-gray-100 disabled:opacity-40';

const TipTapScopeNotes = ({
  value,
  onChange,
  readOnly = false,
  label = 'Notes',
  minHeight = 160,
  className = '',
}) => {
  const doc =
    value?.format === 'tiptap' && value?.doc
      ? value.doc
      : value?.type === 'doc'
        ? value
        : emptyTipTapDoc();

  const lastEmittedRef = useRef('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        code: false,
        strike: false,
      }),
      Underline,
    ],
    content: doc,
    editable: !readOnly,
    onUpdate: ({ editor: ed }) => {
      if (readOnly || !onChange) return;
      const nextDoc = ed.getJSON();
      const serialized = JSON.stringify(nextDoc);
      // Ignore setContent / identical emits so autosave is not spammed.
      if (serialized === lastEmittedRef.current) return;
      lastEmittedRef.current = serialized;
      onChange({ format: 'tiptap', doc: nextDoc });
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none px-3 py-2 focus:outline-none font-["Open_Sans"] text-black',
        style: `min-height:${typeof minHeight === 'number' ? `${minHeight}px` : minHeight}`,
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  useEffect(() => {
    if (!editor || !value) return;
    // Don't fight the user while they're typing — autosave merges can
    // deliver a new object reference with the same (or slightly older) doc.
    if (editor.isFocused) return;
    const nextDoc =
      value?.format === 'tiptap' && value?.doc
        ? value.doc
        : value?.type === 'doc'
          ? value
          : emptyTipTapDoc();
    const current = JSON.stringify(editor.getJSON());
    const next = JSON.stringify(nextDoc);
    if (current !== next) {
      lastEmittedRef.current = next;
      editor.commands.setContent(nextDoc, false);
    }
  }, [editor, value]);

  useEffect(() => {
    if (!editor) return;
    lastEmittedRef.current = JSON.stringify(editor.getJSON());
  }, [editor]);

  if (!editor) return null;

  return (
    <div className={className}>
      {label ? <label className={LABEL_CLASS}>{label}</label> : null}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        {!readOnly && (
          <div className="flex flex-wrap gap-1 border-b border-gray-200 px-2 py-1.5 bg-gray-50">
            <button
              type="button"
              className={`${toolbarBtn} ${editor.isActive('bold') ? 'bg-teal-50 border-[#6BABA0]' : ''}`}
              onClick={() => editor.chain().focus().toggleBold().run()}
            >
              B
            </button>
            <button
              type="button"
              className={`${toolbarBtn} italic ${editor.isActive('italic') ? 'bg-teal-50 border-[#6BABA0]' : ''}`}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            >
              I
            </button>
            <button
              type="button"
              className={`${toolbarBtn} underline ${editor.isActive('underline') ? 'bg-teal-50 border-[#6BABA0]' : ''}`}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            >
              U
            </button>
            <button
              type="button"
              className={`${toolbarBtn} ${editor.isActive('bulletList') ? 'bg-teal-50 border-[#6BABA0]' : ''}`}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
              • List
            </button>
            <button
              type="button"
              className={`${toolbarBtn} ${editor.isActive('orderedList') ? 'bg-teal-50 border-[#6BABA0]' : ''}`}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
              1. List
            </button>
          </div>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default TipTapScopeNotes;
